<template>
    <div v-if="show" class="modal-backdrop" @click.self="$emit('close')">
        <div class="modal-content">
            <div class="modal-header">
                <h3>{{ editedTask?.id ? 'Edit Task' : 'Create Task' }}</h3>
                <button class="close-button" @click="$emit('close')">&times;</button>
            </div>

            <div class="modal-body">
                <form @submit.prevent="saveTask">
                    <div class="form-group">
                        <label for="title">Task Title</label>
                        <input 
                            type="text" 
                            id="title" 
                            v-model="editedTask.title"
                            required
                            class="form-control"
                        >
                    </div>

                    <div class="form-group">
                        <label for="description">Description</label>
                        <textarea 
                            id="description" 
                            v-model="editedTask.description"
                            class="form-control"
                            rows="3"
                        ></textarea>
                    </div>

                    <div class="form-group">
                        <label for="relatedTo">Related To</label>
                        <select 
                            id="relatedTo" 
                            v-model="editedTask.related_to_user_id"
                            class="form-control"
                        >
                            <option v-for="user in users" 
                                    :key="user.id" 
                                    :value="user.id">
                                {{ user.display_name }}
                            </option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label for="dueDate">Due Date</label>
                        <input 
                            type="date" 
                            id="dueDate" 
                            v-model="editedTask.due_date"
                            class="form-control"
                            required
                        >
                    </div>

                    <div class="form-group">
                        <label for="priority">Priority</label>
                        <select 
                            id="priority" 
                            v-model="editedTask.priority"
                            class="form-control"
                            required
                        >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label for="status">Status</label>
                        <select 
                            id="status" 
                            v-model="editedTask.status"
                            class="form-control"
                        >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="secondary" @click="$emit('close')">
                            Cancel
                        </button>
                        <button type="submit" class="primary">
                            {{ editedTask?.id ? 'Save Changes' : 'Create Task' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';
import { userService } from '../../services/userService';
import { taskService } from '../../services/taskService';

export default defineComponent({
    name: 'TaskEditModal',
    
    props: {
        show: {
            type: Boolean,
            required: true
        },
        task: {
            type: Object,
            default: null
        },
        userId: {
            type: Number,
            required: true
        }
    },
    emits: ['close', 'task-updated'],

    setup(props, { emit }) {
        const formatDateForInput = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        };

        const formatDateForApi = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.toISOString();
        };

        const editedTask = ref({
            id: null,
            title: '',
            description: '',
            due_date: null,
            status: 'pending',
            priority: 'medium',
            assigned_to: props.userId,
            related_to_user_id: null
        });
        const users = ref([]);
        const loading = ref(false);
        const error = ref(null);

        const loadUsers = async () => {
            try {
                const response = await userService.getMembers();
                users.value = response;
            } catch (error) {
                console.error('Error loading users:', error);
            }
        };

        const initializeTask = () => {
            if (props.task) {
                editedTask.value = {
                    ...editedTask.value,
                    ...props.task,
                    due_date: formatDateForInput(props.task.due_date)
                };
            }
        };

        const saveTask = async () => {
            try {
                loading.value = true;
                error.value = null;

                const taskData = {
                    title: editedTask.value.title,
                    description: editedTask.value.description || '',
                    related_to_user_id: editedTask.value.related_to_user_id || null,
                    due_date: formatDateForApi(editedTask.value.due_date),
                    priority: editedTask.value.priority || 'medium',
                    status: editedTask.value.status || 'pending',
                    assigned_to: props.userId
                };

                if (editedTask.value.id) {
                    await taskService.updateTask(editedTask.value.id, taskData);
                } else {
                    await taskService.createTasks(taskData);
                }

                emit('task-updated');
                emit('close');
            } catch (err) {
                console.error('Error saving task:', err);
                error.value = 'Failed to save task';
            } finally {
                loading.value = false;
            }
        };

        onMounted(async () => {
            await loadUsers();
            initializeTask();
        });

        // Watch for changes in props.task
        watch(() => props.task, () => {
            initializeTask();
        });

        return {
            editedTask,
            users,
            loading,
            error,
            saveTask
        };
    }
});
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 1rem;
    box-sizing: border-box;
}

.modal-content {
    background: white;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #eee;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    color: #2c3e50;
}

.modal-body {
    padding: 1.5rem;
}

.close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    margin: -0.5rem -0.5rem -0.5rem 0;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-weight: 500;
}

.form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}

textarea.form-control {
    min-height: 100px;
    resize: vertical;
}

select.form-control {
    background-color: white;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
}

button {
    padding: 0.5rem 1rem;
    border-radius: 4px;
    border: none;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
}

button.secondary {
    background-color: #6c757d;
    color: white;
}

button.primary {
    background-color: #4CAF50;
    color: white;
}

button:hover {
    opacity: 0.9;
}

@media (max-width: 768px) {
    .modal-backdrop {
        padding: 0.5rem;
    }
    
    .modal-content {
        max-height: calc(100vh - 2rem);
    }
    
    .modal-body {
        padding: 1rem;
    }
}
</style>
